import { ImmunizationRecord, ImmunizationSeries } from "../models/Interfaces";
import React, { useEffect } from "react";
import { infoLog } from "../utils/logger";
import { useTranslation } from "react-i18next";
import "./recordCard.css";
import { useNavigate } from "react-router";
import getIzProvider, { IzProvider } from "../models/providers/IzProviders";
import { getStatusColor, getStatusLabel } from "../utils/status";
import { useAtom } from "jotai";
import { izProviderConfigsAtom } from "../jotai/atoms";
import { getDateTime } from "../utils/data";
import { DateTime } from "luxon";
import { APP_DOMAIN, PROTOCOL } from "../globals";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

interface RecordCardProps {
  index: number;
  record: ImmunizationRecord;
  series: ImmunizationSeries;
  onSelect: Function;
}

// Virus images
const adenovirus = "/assets/images/Adenovirus.png";
const anthrax = "/assets/images/Anthrax.png";
const cholera = "/assets/images/Cholera.png";
const covid = "/assets/images/Covid19.png";
const dtap = "/assets/images/DTap.png";
const generic = "/assets/images/Default.png";
const hepa = "/assets/images/HepatitisA.png";
const hepb = "/assets/images/HepatitisB.png";
const hib = "/assets/images/HIB.png";
const hpv = "/assets/images/HumanPapilloma.png";
const influenza = "/assets/images/Influenza.png";
const japanese_encephalitis = "/assets/images/Japanese_Encephalitis.png";
const meningococcal = "/assets/images/Meningococcal.png";
const mmr = "/assets/images/MMR.png";
const monkeypox = "/assets/images/monkeypox.png";
const pneumococcal = "/assets/images/Pneumococcal.png";
const polio = "/assets/images/Polio.png";
const rabies = "/assets/images/rabies.png";
const rotavirus = "/assets/images/Rotavirus.png";
const rsv = "/assets/images/RSV.png";
const tb = "/assets/images/Tuberculosis.png";
const typhoid = "/assets/images/typhoid.png";
const varicella = "/assets/images/Varicella.png";
const yellow_fever = "/assets/images/yellow_fever.png";
const zoster = "/assets/images/zoster.png";

const update = "/assets/images/njUpdate2.png";

// TODO: i18n - We should make this part of the i18n files and just swap their paths
const overdue_es = "/assets/images/atrasada.png";
const record_es = "/assets/images/record_es.png";
const complete_es = "/assets/images/completa.png";
const current_es = "/assets/images/aldia.png";
const immune_es = "/assets/images/immuneStatusEs.png";
const dueNow_es = "/assets/images/letocaahora.png";

const qr_blue = "/assets/images/Icon_QR_Code_Blue.png";
const qr_red = "/assets/images/Icon_QR_Code_Red.png";
const qr_green = "/assets/images/Icon_QR_Code_Green.png";
const qr_yellow = "/assets/images/Icon_QR_Code_Yeller.png";

const calendar_red = "/assets/images/Icon_Calendar_DUE.png";
const calendar_yellow = "/assets/images/Icon_Calendar_DUE-NOW.png";
const calendar_green = "/assets/images/Icon_Calendar_CURRENT.png";

export default function RecordCard({ record, series, index, onSelect }: RecordCardProps) {
  const [providerConfigs] = useAtom(izProviderConfigsAtom);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const status = series.status.toLowerCase();
  const provider = record.izProviderKey.toLowerCase();
  const izProvider = getIzProvider(record.izProviderKey, t);
  const seriesDate = series.dateDue ? series.dateDue : series.earliestDateToGive;

  const providerEnabledQrCode = () => {
    const matchingProviderConfigs = providerConfigs.filter(
      (pc) => pc.iz_provider.destination_id.toLowerCase() === record.izProviderKey.toLowerCase()
    );
    if (matchingProviderConfigs.length > 1) {
      throw new Error(
        `Expected to only match 1 IZ provider when determining whether or not to show a QR code, found ${matchingProviderConfigs.length}`
      );
    }

    if (matchingProviderConfigs.length === 0) {
      // We can't find this provider, so disable it by default
      return false;
    }

    const config = matchingProviderConfigs[0];
    return config.features && config.features.qr_code;
  };

  const supportsCovidQRCode = () => {
    return (
      series.vaccineType.toLowerCase().includes("covid") &&
      series.events.length > 0 &&
      providerEnabledQrCode()
    );
  };

  const getShotLabel = () => {
    const shotCount = series.events.length;
    return provider === "ak"
      ? `${t("immunizations.records_doses", { count: shotCount })}`
      : `${t("immunizations.records_shots", { count: shotCount })}`;
  };

  const getVirusImage = () => {
    let virus = series.vaccineType.toLowerCase();
    if (virus === "hep-a" || virus === "hep a" || virus === "hepa" || virus === "hepatitis a") {
      return hepa;
    } else if (
      virus === "hep-b" ||
      virus === "hep b" ||
      virus === "hepb" ||
      virus === "hepatitis b"
    ) {
      return hepb;
    } else if (
      virus.includes("easles") ||
      virus.includes("mumps") ||
      virus.includes("rubella") ||
      virus.includes("mmr")
    ) {
      return mmr;
    } else if (virus.toLowerCase().includes("covid")) {
      return covid;
    } else if (virus === "hib" || virus === "haemophilus influenzae b") {
      return hib;
    } else if (virus.includes("monkey") || virus.includes("small")) {
      //or smallpox
      return monkeypox;
    } else if (virus.includes("pox") || virus.includes("var")) {
      //chickenpox
      //top = -3.8
      return varicella;
    } else if (virus.includes("flu")) {
      //top = -3.5
      return influenza;
    } else if (
      virus.includes("td") ||
      virus.includes("dtp") ||
      virus.includes("tap") ||
      virus === "diphtheria, tetanus, pertussis"
    ) {
      return dtap;
    } else if (virus.includes("gococcal") || virus.includes("mening")) {
      return meningococcal;
    } else if (virus.includes("neumo")) {
      return pneumococcal;
    } else if (virus.includes("roto") || virus.includes("rota")) {
      return rotavirus;
    } else if (virus.includes("polio")) {
      return polio;
    } else if (virus.includes("COVID") || virus.includes("-19")) {
      return covid;
    } else if (virus.includes("hpv")) {
      return hpv;
    } else if (virus.includes("zoster")) {
      return zoster;
    } else if (virus.includes("typhoid")) {
      return typhoid;
    } else if (virus.includes("rabies")) {
      return rabies;
    } else if (virus.includes("encephalitis")) {
      return japanese_encephalitis;
    } else if (virus.includes("yellow")) {
      return yellow_fever;
    } else if (
      virus.toLowerCase().includes("tuberculosis") ||
      virus.toLowerCase().includes("bsv")
    ) {
      return tb;
    } else if (
      virus.toLowerCase().includes("rsv") ||
      virus.toLowerCase().includes("respiratory syncytial virus")
    ) {
      return rsv;
    } else if (virus.toLowerCase().includes("cholera")) {
      return cholera;
    } else if (virus.toLowerCase().includes("adenovirus")) {
      return adenovirus;
    } else if (virus.toLowerCase().includes("anthrax")) {
      return anthrax;
    }

    return generic;
  };

  const getQrImage = () => {
    let img = qr_blue;
    if (status === "overdue" || (status === "due_now" && izProvider.statusImages.redOnDue)) {
      img = qr_red;
    } else if (status === "due_now") {
      img = qr_yellow;
    } else if (status === "current" || status === "complete") {
      img = qr_green;
    }
    return img;
  };

  const getCalendarImage = () => {
    const status = series.status.toLowerCase();

    if (status === "overdue") {
      return calendar_red;
    } else if (status === "due_now") {
      return calendar_yellow;
    } else if (status === "current") {
      return calendar_green;
    }

    return "";
  };

  const getRecordedInText = () => {
    // This exception exists here since we have to embed the QR code
    // next to COVID, which was pushing text out too much on the mobile app.
    // This may not be needed since our UI is different in the PWA.
    let msg = t("immunizations.recorded_in", { iisAbbrev: izProvider.iisAbbrev });
    if (supportsCovidQRCode()) {
      msg = ` IN ${izProvider.iisAbbrev}`;
    }
    return msg;
  };

  const readableDate = () => {
    const date = series.dateDue ? series.dateDue : series.earliestDateToGive;
    if (
      date &&
      (status === "overdue" || status === "due_now" || status === "due" || status === "current")
    ) {
      // "CURRENT" vaccines may not have an upcoming due date.
      return (
        <>
          <p className="font-semibold text-sm">{t("immunizations.records_due_on")}</p>
          <p className="text-sm">{getDateTime(date).toLocaleString()}</p>
        </>
      );
    }

    // need something here to keep height consistent, using spaces...
    return (
      <>
        <p className="max-md:hidden text-sm">{t("immunizations.records_no_due_date")}</p>
        <p className="text-sm md:hidden">&nbsp;</p>
      </>
    );
  };

  const hasFutureDate = () => {
    if (!seriesDate) {
      return false;
    }

    return getDateTime(seriesDate) > DateTime.now();
  };

  const getTitleText = (text: string) => {
    let newText = t("immunizations.records_processing");
    if (text && text !== "wait") {
      newText = text.length > 23 ? text.substring(0, 20) + "..." : text;
    }
    return newText;
  };

  const goToEvents = () => {
    if (series.events.length > 0) {
      navigate("events", {
        state: {
          record: record,
          series: series,
          back: true,
          title: `${record.firstName} ${record.lastName}`,
        },
      });
    }
  };

  const goToSmartHealthCard = () => {
    navigate(`${record.uid}/smart-health-card`, {
      state: {
        back: true,
        title: "SMART® Health Card",
      },
    });
  };

  const addToCalendar = () => {
    if (!seriesDate) return false;

    const eventTitle = "Immunization Reminder";
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const eventDescription =
      "Docket® Immunization Reminder: Please check your Docket® account for more information about your recommended immunization(s). Be sure to consult a healthcare provider before getting a new vaccine.";

    const seriesDateTime = getDateTime(seriesDate);
    const currDate = DateTime.now().toFormat("yyyyLLdd'T'HHmmss");
    const eventStart = seriesDateTime.toFormat("yyyyLLdd");
    const eventEnd = seriesDateTime.plus({ days: 1 }).toFormat("yyyyLLdd");
    const uniqueId = `${seriesDateTime.toFormat("yyyyLLdd'T'HHmmss")}-${currDate}`;

    const icsData = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "PRODID:-//Docket//Docket Immunization Reminder//EN",
      `X-WR-TIMEZONE:${timeZone}`,
      "BEGIN:VEVENT",
      `DTSTART;VALUE=DATE:${eventStart}`,
      `DTEND;VALUE=DATE:${eventEnd}`,
      `DTSTAMP:${currDate}`,
      `UID:${uniqueId}`,
      `CREATED:${currDate}`,
      `DESCRIPTION:${eventDescription}`,
      `SUMMARY:${eventTitle}`,
      "TRANSP:TRANSPARENT",
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder",
      "TRIGGER;RELATED=START:-PT0M",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const calendarLink = `data:text/calendar,${encodeURIComponent(icsData)}`;
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: `${PROTOCOL}://${APP_DOMAIN}/add-event?link=${calendarLink}` });
    } else {
      window.open(`/add-event?link=${calendarLink}`, "_blank", "rel=noopener noreferrer");
    }
  };

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  const statusLabel = getStatusLabel(izProvider, status, series);

  return (
    <div className="">
      <div className="flex record-card max-md:py-2 mobile-card sm:h-[5em] md:h-[7em]">
        <div className={`${getStatusColor(izProvider, status, series)} record-badge-bg`}></div>
        <div
          onClick={() => goToEvents()}
          className={
            `record-badge text-white text-xs ml-1 font-semibold` +
            (statusLabel === "record" ? "" : " dark:text-dark")
          }
        >
          {t("immunizations.records_status", {
            context: statusLabel,
          })}
        </div>

        <div
          onClick={() => goToEvents()}
          className="ml-12 my-auto mr-auto md:p-3 flex flex-col gap-0.5"
        >
          <div
            role="paragraph"
            className="card-title text-dark text-sm font-semibold"
            data-testid="record-card-title"
          >
            {getTitleText(series.vaccineType)}
          </div>
          <div
            role="paragraph"
            className="flex gap-0.5 text-dark"
            data-testid="record-card-description"
          >
            {readableDate()}
          </div>
          <div
            role="paragraph"
            className="text-xs font-bold record-card-description"
            data-testid="record-card-description"
          >
            {`${getShotLabel()} ${getRecordedInText()}`}
          </div>
        </div>
        {hasFutureDate() && (
          <div onClick={() => addToCalendar()} className="my-auto">
            <img
              className="record-card-right-img"
              src={getCalendarImage()}
              alt={`Calendar Image`}
            />
          </div>
        )}
        {supportsCovidQRCode() && (
          <div onClick={goToSmartHealthCard} className="my-auto mr-2">
            <img className="record-card-right-img" src={getQrImage()} alt="Placeholder image" />
          </div>
        )}
        <div onClick={() => goToEvents()} className="my-auto md:mr-6">
          <img
            className="record-card-right-img"
            src={getVirusImage()}
            alt={`${series.vaccineType.toLowerCase()} Image`}
          />
        </div>
      </div>
    </div>
  );
}
